import { useEffect } from "react";

type GTMComponentProps = {
  onConfigLoad: (config: any) => void;
};

const GTM_ID = process.env.REACT_APP_GTM_ID;
const MEASUREMENT_ID = process.env.REACT_APP_MEASUREMENT_ID || "";

const addGtagScript = (measurementId: string) => {
  if (!measurementId) {
    console.error("Measurement ID is not defined.");
    return;
  }

  const gtagScript = document.createElement("script");
  gtagScript.async = true;
  gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(gtagScript);

  const gtagInitScript = document.createElement("script");
  gtagInitScript.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      dataLayer.push(arguments);
    }
    gtag("js", new Date());
    gtag("config", "${measurementId}");
  `;
  document.head.appendChild(gtagInitScript);
};

const GTMComponent: React.FC<GTMComponentProps> = ({ onConfigLoad }) => {
  useEffect(() => {
    let retryCount = 0;
    const MAX_RETRIES = 5;
    const RETRY_DELAY = 1000;
    let timeoutId: NodeJS.Timeout | null = null;
    let configLoaded = false;

    const loadFromDataJson = async () => {
      if (configLoaded) return;

      try {
        const response = await fetch("/data.json");
        if (response.ok) {
          const configData = await response.json();
          configLoaded = true;
          console.log("Loaded config from data.json fallback");
          onConfigLoad(configData);
        } else {
          console.error("Failed to load data.json");
        }
      } catch (error) {
        console.error("Error loading data.json:", error);
      }
    };

    const checkConfig = () => {
      const w = window as any;
      if (w && w.config) {
        const slides = w.config;
        configLoaded = true;
        onConfigLoad({ slides });
      } else {
        retryCount++;
        if (retryCount < MAX_RETRIES) {
          console.warn(`Config not available. Retrying... (${retryCount}/${MAX_RETRIES})`);
          timeoutId = setTimeout(checkConfig, RETRY_DELAY);
        } else {
          console.warn("GTM config failed to load after max retries. Falling back to data.json");
          loadFromDataJson();
        }
      }
    };

    // Try to load GTM script if GTM_ID is available
    if (GTM_ID) {
      const gtmScript = document.createElement("script");
      gtmScript.innerHTML = `
        (function(w, d, s, l, i) {
          w[l] = w[l] || [];
          w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
          var f = d.getElementsByTagName(s)[0],
            j = d.createElement(s),
            dl = l != "dataLayer" ? "&l=" + l : "";
          j.async = true;
          j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
          f.parentNode.insertBefore(j, f);
        })(window, document, "script", "dataLayer", "${GTM_ID}");
      `;
      document.head.appendChild(gtmScript);

      const noscriptFrame = document.createElement("noscript");
      noscriptFrame.innerHTML = `
        <iframe src="https://www.googletagmanager.com/ns.html?id=${GTM_ID}"
          height="0" width="0" style="display: none; visibility: hidden"></iframe>
      `;
      document.body.appendChild(noscriptFrame);

      // Start checking for config
      checkConfig();

      // Cleanup function
      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (document.head.contains(gtmScript)) {
          document.head.removeChild(gtmScript);
        }
        if (document.body.contains(noscriptFrame)) {
          document.body.removeChild(noscriptFrame);
        }
      };
    } else {
      // No GTM_ID, load directly from data.json
      console.log("No GTM_ID configured. Loading from data.json");
      loadFromDataJson();
    }

    // Add gtag script for analytics (if measurement ID is available)
    if (MEASUREMENT_ID) {
      addGtagScript(MEASUREMENT_ID);
    }
  }, [onConfigLoad]);

  return null;
};

export default GTMComponent;
